import {createStore, filterNil} from '@ngneat/elf';
import {deleteEntities, selectEntity, selectManyByPredicate, withEntities} from "@ngneat/elf-entities";
import {firstValueFrom, Observable} from "rxjs";
import {Injectable, signal} from "@angular/core";
import {map, tap} from "rxjs/operators";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, Feilhåndteringsstrategi, forelderIdPredikat, MedForelderId, oppdaterEntitetMedForelder, registrerEntiteterMedForelder, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, serverFraRequestPakketIMeta, slettAlleEntiteterMedForeldre, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {HttpResponse} from "@angular/common/http";
import {DataOgMeta, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {GruppeId, GruppeInvitasjonRessursClient, GruppeInvitasjonSpesifikasjoner, GruppeMedlemListeModell, GruppeMedlemRessursClient, GruppeModell, GruppeRessursClient, InvitasjonSlettetRespons, InvitasjonSlettSpesifikasjon, MedlemAnonymiserSpesifikasjon, MedlemId, MedlemStengTilgangSpesifikasjon, MedlemÅpneTilgangSpesifikasjon, OppdaterMedlemSpesifikasjon, OpprettGruppeInvitasjonSpesifikasjon, RedigerGruppeInvitasjonSpesifikasjon} from "../ressurser/generertNg2HttpKlient";
import {medlemmerAktive, medlemmerAnonymisert, medlemmerMedTilgangMedBrukertilknytning, medlemmerMedTilgangUtenBrukertilknytning, medlemmerUtenTilgangMedBrukertilknytning, sorterMedlemPåAktivMedlemsrolleOgNavn} from "felles-ng2/modell/medlemModellHjelpere";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";

const debug = initDebugger('GruppemedlemDatatjeneste');

/**
 * Datalager for {@link GruppeMedlemListeModell}.
 */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class GruppemedlemDatatjeneste {

    /** Lager for {@link GruppeMedlemListeModell}. Bruker {@link GruppeId} som forelder */
    private gruppemedlemEnkelLager = createStore(
      {name: 'gruppemedlemEnkel'},
      withEntities<GruppeMedlemListeModell>());

    // datatjenestene under må settes etter opprettelse av instans pga sirkularitet
    private gruppeDatatjeneste!: GruppeDatatjeneste;

    constructor(private gruppemedlemRessurs: GruppeMedlemRessursClient,
                private gruppeInvitasjonRessurs: GruppeInvitasjonRessursClient,
                private gruppeRessurs: GruppeRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser) {
    }

    /** For intern bruk. Datatjenestene under må settes etter opprettelse av instans pga sirkularitet */
    setDatatjenester(gruppeDatatjeneste: GruppeDatatjeneste) {
        this.gruppeDatatjeneste = gruppeDatatjeneste;
    }

    hentMedMeta(id: MedlemId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<GruppeMedlemListeModell>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.gruppemedlemRessurs.hent(id),
            registrerILager: (respons: HttpResponse<GruppeMedlemListeModell>) => this.registererMedlemIGruppemedlemEnkelLager(respons),
            hentFraLager: () => this.gruppemedlemEnkelLager.pipe(selectEntity(id), filterNil()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente medlem', 'hentMedMeta')
        });
    }

    hent(id: MedlemId, feilhåndteringsstrategi = Feilhåndteringsstrategi.visFeilmelding): Observable<GruppeMedlemListeModell> {
        return this.hentMedMeta(id, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Alle medlemmer tilknyttet angitt gruppe. */
    finnTilknyttetMedMeta(gruppeId: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<GruppeMedlemListeModell[]>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.hent(gruppeId),
            registrerILager: (respons: HttpResponse<GruppeModell>) => this.registererGruppeIGruppemedlemEnkelLager(respons),
            hentFraLager: () => this.gruppemedlemEnkelLager.pipe(
              selectManyByPredicate(forelderIdPredikat(gruppeId)),
              sorterLevertSamling(standardSortering)),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente medlemmer")), 'finnTilknyttetMedMeta')
        });
    }

    /** Alle medlemmer tilknyttet angitt gruppe. */
    finnTilknyttet(gruppeId: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<GruppeMedlemListeModell[]> {
        return this.finnTilknyttetMedMeta(gruppeId, feilhåndteringsstrategi)
          .pipe(mapTilData())
    }

    /** Aktive medlemmer som ikke har avslått invitasjon, tilknyttet angitt gruppe. */
    finnTilgjengeligeMedMeta(gruppeId: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<GruppeMedlemListeModell[]>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.gruppeRessurs.finnTilgjengeligeMedlemmer(gruppeId),
            registrerILager: (respons: HttpResponse<GruppeMedlemListeModell[]>) => registrerEntiteterMedForelder(this.gruppemedlemEnkelLager, respons.body!, gruppeId),
            hentFraLager: () => this.gruppemedlemEnkelLager.pipe(
              selectManyByPredicate(forelderIdPredikat(gruppeId)),
              map(medlemmerAktive),
              sorterLevertSamling(standardSortering)),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente medlemmer")), 'finnTilgjengeligeMedMeta')
        });
    }

    /** Aktive medlemmer som ikke har avslått invitasjon, tilknyttet angitt gruppe */
    finnTilgjengelige(gruppeId: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<GruppeMedlemListeModell[]> {
        return this.finnTilgjengeligeMedMeta(gruppeId, feilhåndteringsstrategi)
          .pipe(mapTilData())
    }

    /** Returnerer gruppemedlemmer tilknyttet angitt gruppe registrert i lokallageret.
     * Forutsetter at feks {@link #finnTilknyttet} har blitt kalt i forkant */
    finnTilknyttetFraLokallager(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemEnkelLager.pipe(
          selectManyByPredicate(forelderIdPredikat(gruppeId)),
          sorterLevertSamling(standardSortering)
        );
    }

    /** Erstatter alle medlemmer knytte til angitt gruppeId i lokallager */
    erstattMedlemmerILokallager(gruppeId: GruppeId, medlemmer: GruppeMedlemListeModell[]) {
        erstatteAlleEntiteterMedForelder(this.gruppemedlemEnkelLager, medlemmer, gruppeId);
    }

    /** Sletter alle medlemmer knytte til angitte gruppeIder i lokallager */
    slettMedlemmerTilknyttetGrupperILokallager(gruppeIder: GruppeId[]) {
        slettAlleEntiteterMedForeldre(this.gruppemedlemEnkelLager, gruppeIder);
    }

    fjernTilgang(id: MedlemId, spesifikasjon: MedlemStengTilgangSpesifikasjon = {avsluttedeRolleperioder: [], oppdaterFremtidigeMoeter: true, oppdaterMoetemal: true}, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeMedlemListeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppemedlemRessurs.fjernTilgang(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.registererMedlemIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke stenge tilgang')), 'fjernTilgang')
        }));
    }

    åpneTilgang(id: MedlemId, spesifikasjon: MedlemÅpneTilgangSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeMedlemListeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppemedlemRessurs.åpneTilgang(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.registererMedlemIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke åpne tilgang')), 'fjernTilgang')
        }));
    }

    anonymiser(id: MedlemId, spesifikasjon: MedlemAnonymiserSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeMedlemListeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppemedlemRessurs.anonymiser(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.registererMedlemIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke stenge tilgang permanent')), 'fjernTilgang')
        }));
    }

    oppdaterMedlem(id: MedlemId, spesifikasjon: OppdaterMedlemSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeMedlemListeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppemedlemRessurs.oppdater(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.registererMedlemIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere medlem')), 'oppdaterMedlem')
        }));
    }

    oppdaterInvitasjon(id: MedlemId, spesifikasjon: RedigerGruppeInvitasjonSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeMedlemListeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeInvitasjonRessurs.oppdaterInvitasjon(id, spesifikasjon).råRespons()
              .pipe(tap(respons => this.registererMedlemIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere invitasjon')), 'oppdaterInvitasjon')
        }));
    }

    slettEllerAnonymiserInvitasjon(id: MedlemId, spesifikasjon: InvitasjonSlettSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<InvitasjonSlettetRespons> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.gruppeInvitasjonRessurs.slettEllerAnonymiser(id, spesifikasjon).råRespons()
              .pipe(
                tap(respons => {
                    const resultat = respons.body!;
                    if (resultat.slettet) this.gruppemedlemEnkelLager.update(deleteEntities(id));
                    else oppdaterEntitetMedForelder(this.gruppemedlemEnkelLager, resultat.anonymisertInvitasjon!, resultat.anonymisertInvitasjon!.gruppeId);
                })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette invitasjon')), 'slettEllerAnonymiserInvitasjon')
        }));
    }

    sendInvitasjon(id: MedlemId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeMedlemListeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeInvitasjonRessurs.sendInvitasjon(id).råRespons()
              .pipe(tap(respons => this.registererMedlemIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke sende invitasjon')), 'sendInvitasjon')
        }));
    }

    inviterNyttMedlem(spesifikasjon: OpprettGruppeInvitasjonSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<GruppeMedlemListeModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.gruppeInvitasjonRessurs.inviterNyttMedlem(spesifikasjon).råRespons()
              .pipe(tap(respons => this.registererMedlemIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere invitasjon')), 'inviterNyttMedlem')
        }));
    }

    inviterNyeMedlemmer(spesifikasjon: GruppeInvitasjonSpesifikasjoner, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<GruppeModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.gruppeInvitasjonRessurs.inviterNyeMedlemmer(spesifikasjon).råRespons()
              .pipe(tap(respons => this.registererGruppeIGruppemedlemEnkelLager(respons))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere invitasjoner')), 'inviterNyeMedlemmer')
        }));
    }


    //hjelpemetoder

    private registererMedlemIGruppemedlemEnkelLager(respons: HttpResponse<GruppeMedlemListeModell>): (GruppeMedlemListeModell & MedForelderId) {
        var medlem = respons.body!;
        return registrerEntitetMedForelder(this.gruppemedlemEnkelLager, medlem, medlem.gruppeId);
    }

    private registererGruppeIGruppemedlemEnkelLager(respons: HttpResponse<GruppeModell>): (GruppeMedlemListeModell & MedForelderId)[] {
        var gruppe = respons.body!;
        //oppdaterer gruppe i gruppeDatatjeneste, merk at dette i sin tur vil fører til at erstattMedlemmerILokallager vil
        //bli kalt pga propagering via lager-eventer (se GruppeDatatjeneste.propagerEndringerTilAndreDatatjenester())
        this.gruppeDatatjeneste.erstattILokallager(gruppe);
        return registrerEntiteterMedForelder(this.gruppemedlemEnkelLager, gruppe.alleMedlemmer, gruppe.id);
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(feilhåndteringsstrategi: STRATEGI,
                                                                               brukerrettetFeilmelding: string,
                                                                               metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }
}

const standardSortering = sorterMedlemPåAktivMedlemsrolleOgNavn;

export class MedlemmerGruppert {

    /** medlemmer som har tilgang og er knyttet til bruker */
    medTilgangMedBrukertilknytning = signal<GruppeMedlemListeModell[]>([]);
    /** medlemmer som har tilgang og IKKE er knyttet til bruker, normalt kalt invitasjoner */
    medTilgangUtenBrukertilknytning = signal<GruppeMedlemListeModell[]>([]);
    /** medlemmer som IKKE har tilgang og er knyttet til bruker */
    utenTilgangMedBrukertilknytning = signal<GruppeMedlemListeModell[]>([]);
    /** medlemmer som har blitt anonymisert, disse har ikke tilgang og er ikke tilknyttet noen bruker */
    anonymiserte = signal<GruppeMedlemListeModell[]>([]);

    oppdater(medlemmer: GruppeMedlemListeModell[]) {
        this.medTilgangMedBrukertilknytning.set(medlemmerMedTilgangMedBrukertilknytning(medlemmer));
        this.medTilgangUtenBrukertilknytning.set(medlemmerMedTilgangUtenBrukertilknytning(medlemmer));
        this.utenTilgangMedBrukertilknytning.set(medlemmerUtenTilgangMedBrukertilknytning(medlemmer));
        this.anonymiserte.set(medlemmerAnonymisert(medlemmer));
    }
}
